

import { Getter, State } from "vuex-class"
import { Component, Vue, Prop, Watch } from "vue-property-decorator"

// ---- Libraries ----
import cloneDeep from "lodash/cloneDeep"
import { DateTime } from "luxon"
import { formatDateMixin } from "@/mixins/formatDateMixin"

// ---- Types ----
import { TimeZone } from "@/types"
import { UserScheduleStatus, UserStatusScheduleView } from "@/types/userSchedules"
import { UserStatus } from "@/types/userStatus"
import { TenantFeatures } from "@/types/tenantFeatures"

// ---- Components ----
// @ts-ignore
import VDatePicker from "v-calendar/lib/components/date-picker.umd"
import vSelect from "vue-select"
import DataTable from "@/components/Config/DataTable.vue"

// ---- Services ----
import apiUserStatus from "@/services/api_UserStatus"

@Component({
	components: {
		"data-table": DataTable,
		"v-select": vSelect
	}
})
export default class UserSchedules extends Vue {
	// ---- Props ----

	@Prop({ type: String, required: true })
	userId!: string

	@Prop({ type: Boolean, default: true })
	readonly!: boolean

	@State private readonly displayTimezone!: TimeZone | null

	// ---- VueX ----

	@Getter("getTenantTimezones")
	public tenantTimeZones!: TimeZone[]
	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	// ---- Data ----

	statuses: UserStatus[] = []
	userSchedules: UserStatusScheduleView[] = []
	collapsed: boolean = false
	resetStatusId: string | null = null
	disabledDatesOffset: number = 0

	sortBy: keyof UserStatusScheduleView = "startDate"

	canDeleteItem = () => !this.readonly

	// ---- Computed Properties ----

	/**
	 * List of table and modal columns
	 */
	get columns () {
		return [
			{
				key: "dateRange",
				label: "Dates",
				inTable: false,
				dynamicComponent: VDatePicker,
				modalColumn: 1,
				componentProperties: {
					"is-expanded": true,
					"is-range": true,
					"timezone": "Etc/UTC",
					"disabled-dates": [
						{ end: new Date(new Date().setDate(new Date().getDate() - this.disabledDatesOffset)) }
					]
				},
				errors: (item: UserStatusScheduleView) => !item.dateRange || !item.dateRange.start || !item.dateRange.end ? "Please select a date range" : null,
				additionalMessage: (item: UserStatusScheduleView) => this.getIsFeatureEnabled(TenantFeatures.UserStatusSchedulesDateRangeOverlapWarning) ? this.validateDateRangeOverlap(item) : null
			},
			{
				key: "timeZone",
				label: "Time Zone",
				sortable: true,
				dataType: "list",
				valueColumn: "timeZone",
				inTable: false,
				options: this.tenantTimeZones,
				errors: (item: UserStatusScheduleView) => (item.timeZone ? null : "Please select a time zone"),
				modalColumn: 2
			}, {

				key: "scheduleStatus",
				label: "Schedule Status",
				sortable: true,
				sortByFormatted: true,
				inModal: () => false,
				inTable: this.getIsFeatureEnabled(TenantFeatures.DisplayUserScheduleStatus),
				formatter: (value: UserScheduleStatus, key: string, item: UserStatusScheduleView) => this.getUserScheduleStatusTitle(item)
			},
			{
				key: "startDate",
				label: "Start Date",
				sortable: true,
				inModal: () => false
			},
			{
				key: "startStatusId",
				label: "Start Status",
				dataType: "list",
				options: this.statuses,
				modalColumn: 2,
				sortable: true,
				sortByFormatted: true,
				formatter: (v: string) => v && this.statusesById[v] ? this.statusesById[v].title : "Unknown status",
				errors: (item: UserStatusScheduleView) => !item.startStatusId ? "Please select a starting status" : null
			},
			{
				key: "endDate",
				label: "End Date",
				sortable: true,
				inModal: () => false
			},
			{
				key: "endStatusId",
				label: "End Status",
				dataType: "list",
				options: this.statuses,
				modalColumn: 2,
				sortable: true,
				sortByFormatted: true,
				formatter: (v: string) => v && this.statusesById[v] ? this.statusesById[v].title : "Unknown status",
				errors: (item: UserStatusScheduleView) => !item.endStatusId ? "Please select an ending status" : null
			},
			{
				key: "note",
				label: "Note",
				dataType: "text-area",
				modalColumn: 2,
				tdClass: "wrap-text",
				sortable: true
			}
		]
	}

	/**
	 * Map of user statuses by ID
	 */
	get statusesById (): { [key: string]: UserStatus } {
		return this.statuses.toDictionary("id")
	}

	/**
	 * Columns to display in table
	 */
	get tableColumns () {
		return this.columns.filter(c => c.inTable !== false)
	}

	/**
	 * Current user's display time zone setting in IANA format
	 */
	private get currentDisplayTimeZoneIana (): string {
		return this.displayTimezone ? this.displayTimezone.iana : "None"
	}

	// ---- Lifecycle Hooks ----

	mounted () {
		apiUserStatus.userStatusList().then(statuses => this.statuses = statuses)
		this.loadSchedules()
	}

	// ----Watchers ----

	/**
	 * Reloads user schedules when user ID parameter changes
	 */
	@Watch("userId")
	onUserIdChanged () {
		this.loadSchedules()
	}

	// ---- Event Handlers ----

	/**
	 * Currently handles the change event for the schedule by updating the disabled dates offset
	 * based on the selected time zone. This method could be extended to handle all schedule changes if required.
	 * @param schedule - The user schedule object containing the updated time zone.
	 */
	async handleUnsavedItemChange (schedule: UserStatusScheduleView) {
		await this.$nextTick()
		this.disabledDatesOffset = this.calculateDisabledDatesOffset(schedule.timeZone!)
	}

	/**
	 * Prepares a schedule for edit
	 * @param schedule Schedule to load for editing
	 */
	loadItem (schedule: UserStatusScheduleView) {
		const clone = cloneDeep(schedule)

		clone.dateRange = {
			start: (clone.startDate ? DateTime.fromISO(clone.startDate) : DateTime.utc()).toJSDate(),
			end: (clone.endDate ? DateTime.fromISO(clone.endDate) : DateTime.utc().plus({ days: 7 })).toJSDate()
		}

		this.disabledDatesOffset = this.calculateDisabledDatesOffset(clone.timeZone!)

		return clone
	}

	/**
	 * Save a schedule to API and update in list
	 * @param schedule Schedule to save
	 */
	async saveSchedule (schedule: UserStatusScheduleView) {
		if (schedule.dateRange) {
			const startDate = schedule.dateRange!.start!
			const endDate = schedule.dateRange!.end!
			startDate.setUTCHours(0, 0, 0, 0)
			endDate.setUTCHours(0, 0, 0, 0)

			schedule.startDate = startDate!.toISOString()
			schedule.endDate = endDate.toISOString()
			schedule.permanentStartDate = startDate!
		} else {
			schedule.startDate = null
			schedule.endDate = null
			schedule.permanentStartDate = null
		}

		const savedSchedule = await apiUserStatus.userStatusScheduleSave(schedule)
		savedSchedule.scheduleStatus = this.getScheduleStatus(savedSchedule)
		if (savedSchedule.inProgress) {
			const userStatus = this.statuses.find(status => status.id === savedSchedule.startStatusId)
			if (userStatus) {
				this.$emit("userStatusUpdated", this.userId, userStatus)
			}
		}

		this.userSchedules.upsert(savedSchedule, "id")
		this.userSchedules = this.userSchedules.sortBy(this.sortBy)
	}

	/**
	 * Deletes a schedule in API and remove from list
	 * If the schedule is in progress, emits an event to update the user status
	 * @param schedule Schedule to delete
	 */
	async deleteSchedule (schedule: UserStatusScheduleView) {
		const resetStatusId = schedule.inProgress
			? (this.resetStatusId ? this.resetStatusId : schedule.endStatusId)
			: null

		await apiUserStatus.userStatusScheduleDelete(schedule, resetStatusId)

		this.userSchedules.removeWhere(s => s.id === schedule.id)

		if (schedule.inProgress) {
			const userStatus = this.statuses.find(status => status.id === resetStatusId)
			if (userStatus) {
				this.$emit("userStatusUpdated", this.userId, userStatus)
			}
		}
	}

	/**
	 * Returns schedule for delete details
	 * @param schedule Schedule being deleted
	 */
	async deleteDetails (schedule: UserStatusScheduleView) {
		this.resetStatusId = schedule.endStatusId
		return schedule
	}

	/**
	 * Select which status to revert to if a schedule is deleted halfway through
	 * @param resetStatusId Status to reset to
	 */
	setResetStatus (resetStatusId: string | null) {
		this.resetStatusId = resetStatusId
	}

	/**
	 * Start new item for creation
	 */
	createNewItem () {
		this.disabledDatesOffset = 0
		return {
			userId: this.userId
		}
	}

	/**
	 * Name for schedule being deleted
	 * @param schedule Schedule about to be deleted
	 */
	scheduleTitle (schedule: UserStatusScheduleView) {
		return "schedule" +
			(schedule.startDate ? ` starting ${formatDateMixin.methods.displayDateOnlyFromISOString(schedule.startDate)},` : "") +
			(schedule.endDate ? ` ending ${formatDateMixin.methods.displayDateOnlyFromISOString(schedule.endDate)}` : "")
	}

	// ---- Utils ----

	/**
	 * Loads all schedules for current user ID
	 */
	async loadSchedules () {
		this.userSchedules = []
		let loadedUserSchedules = (await apiUserStatus.userStatusScheduleGet(this.userId)).sortBy("startDate")
		// Set schedule status
		if (this.getIsFeatureEnabled(TenantFeatures.DisplayUserScheduleStatus)) {
			loadedUserSchedules.map(schedule => {
				schedule.scheduleStatus = this.getScheduleStatus(schedule)
			})
		}
		this.userSchedules = loadedUserSchedules
	}

	async sortChanged ({ sortBy }: { sortBy: keyof UserStatusScheduleView }) {
		this.sortBy = sortBy
	}

	/**
	 * @summary Converts a UTC date ISO string to its true time zone (same exact date/time values, different time zone) then formats it for
	 * display in the current user's time zone.
	 * @param {string} utcDateIso - The date in UTC time zone.
	 * @param {string} actualTimeZoneId - The actual time zone ID of the provided date.
	 * @param {string} _timeZoneUpdateHook - Not actively used, but required for Vue reactivity when the user overrides their time zone.
	 * @returns Returns a formatted date string containing the date that the provided date&time in the provided time zone will occur in the user's local time.
	 */
	private getFormattedUserScheduleDateString (utcDateIso: string, actualTimeZoneId: string, _timeZoneUpdateHook: string = ""): string {
		if (!utcDateIso) {
			return "Unknown"
		}

		const tenantTimeZone = this.tenantTimeZones.find(tz => tz.id === actualTimeZoneId)

		if (!tenantTimeZone) {
			throw new Error(`Time zone not found: ${actualTimeZoneId}`)
		}

		const dateWithCorrectedTimeZone = DateTime.fromISO(utcDateIso, { setZone: true })
			.setZone(tenantTimeZone.iana, { keepLocalTime: true })

		const correctedDateInLocalTimeIso = dateWithCorrectedTimeZone.toLocal().toISO()

		return formatDateMixin.methods.displayDateOnlyFromISOString(correctedDateInLocalTimeIso)
	}

	/**
	 * Validates the date range and time zone selected to
	 * return a warning message if there is an overlap with
	 * an existing schedule.
	 * @param schedule Schedule to validate
	 */
	private validateDateRangeOverlap (schedule: UserStatusScheduleView): string | null {
		if (!schedule.dateRange || !schedule.timeZone || this.userSchedules.length === 0) {
			return null
		}

		const tenantTimeZone = this.tenantTimeZones.find(tz => tz.id === schedule.timeZone)
		if (!tenantTimeZone) {
			throw new Error(`Time zone not found: ${schedule.timeZone}`)
		}

		const startDate = this.getAdjustedDateWithTimeZone(schedule.dateRange.start!.toISOString(), tenantTimeZone.iana)
		const endDate = this.addDaysToDate(this.getAdjustedDateWithTimeZone(schedule.dateRange.end!.toISOString(), tenantTimeZone.iana), 1)

		for (let i = 0; i < this.userSchedules.length; i++) {
			const existingSchedule = this.userSchedules[i]
			if (existingSchedule.id && existingSchedule.id === schedule.id) {
				continue
			}

			const existingTenantTimeZone = this.tenantTimeZones.find(tz => tz.id === existingSchedule.timeZone)
			if (!existingTenantTimeZone) {
				throw new Error(`Existing time zone not found: ${existingSchedule.timeZone}`)
			}

			const existingStartDate = this.getAdjustedDateWithTimeZone(existingSchedule.startDate!, existingTenantTimeZone.iana)
			const existingEndDate = this.addDaysToDate(this.getAdjustedDateWithTimeZone(existingSchedule.endDate!, existingTenantTimeZone.iana), 1)

			if (
				(startDate < existingEndDate && endDate > existingStartDate) || // Overlap check
				(startDate > existingStartDate && startDate < existingEndDate) || // Start date overlap
				(endDate > existingStartDate && endDate < existingEndDate) // End date overlap
			) {
				return "Warning! The dates above overlap with an existing status schedule."
			}
		}

		return null
	}

	/**
	 * Converts a given date to the beginning of the day in a specified time zone.
	 * @param date The date to convert, provided as an ISO String.
	 * @param timeZone The time zone to use for the conversion.
	 * @returns The converted date as a timestamp.
	 */
	private getAdjustedDateWithTimeZone (date: string, timeZone: string): number {
		const dateTime = DateTime.fromISO(date, { setZone: true })
			.setZone(timeZone, { keepLocalTime: true })
			.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
			.toJSDate()
			.getTime()

		return dateTime
	}

	/**
	 * Adds the specified number of days to the given date and
	 * returns a timestamp of the new date.
	 * @param date The date to add days to, provided as an ISO String.
	 * @param days The number of days to add.
	 * @returns The new date as a timestamp.
	 */
	private addDaysToDate (date: number, days: number): number {
		const newDate = new Date(date)
		newDate.setDate(newDate.getDate() + days)
		return newDate.getTime()
	}

	/**
	 * Calculates the offset for disabled dates based on the comparison
	 * between the current date in a specified time zone and the current UTC date.
	 * @param {string} actualTimeZoneId - The actual time zone ID of the provided date.
	 * @returns - 2 if the tenant's date is earlier than the UTC date,
	 *          - 1 if they are on the same day,
	 *          - 0 if the tenant's date is later than the UTC date.
	 */
	private calculateDisabledDatesOffset (actualTimeZoneId: string): number {
		const tenantTimeZone = this.tenantTimeZones.find(tz => tz.id === actualTimeZoneId)
		if (!tenantTimeZone) {
			throw new Error(`Time zone not found: ${actualTimeZoneId}`)
		}

		const currentDateTimeUTC = DateTime.utc()
		const currentDateTimeTenant = currentDateTimeUTC.setZone(tenantTimeZone.iana)

		// Create Date objects to compare only the day, month and year, ignoring the time.
		const currentDateUTC = new Date(currentDateTimeUTC.year, currentDateTimeUTC.month - 1, currentDateTimeUTC.day)
		const currentDateTenant = new Date(currentDateTimeTenant.year, currentDateTimeTenant.month - 1, currentDateTimeTenant.day)

		if (currentDateTenant < currentDateUTC) {
			return 2
		} else if (currentDateTenant.getTime() === currentDateUTC.getTime()) {
			return 1
		} else {
			return 0
		}
	}

	/**
	 * Returns title for status of User Status Schedule
	 * @param userSchedule The user status schedule
	 */
	private getUserScheduleStatusTitle (userSchedule: UserStatusScheduleView) : string {
		switch (userSchedule.scheduleStatus) {
			case UserScheduleStatus.Upcoming:
				return "Upcoming"
			case UserScheduleStatus.Active:
				return "Active"
			default:
				return "Unknown"
		}
	}

	private getScheduleStatus (schedule: UserStatusScheduleView) {
		const tenantTimeZone = this.tenantTimeZones.find(tz => tz.id === schedule.timeZone)
		if (!tenantTimeZone) {
			throw new Error(`Time zone not found: ${schedule.timeZone}`)
		}

		const currentDateTime = DateTime.fromJSDate(new Date()).setZone(tenantTimeZone.iana).set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
		const startDateTime = DateTime.fromISO(schedule.startDate || "")
		const endDateTime = DateTime.fromISO(schedule.endDate || "")
		// Get only dates for comparison to ensure correct comparison according to selected timezone
		const currentDate = new Date(currentDateTime.year, currentDateTime.month - 1, currentDateTime.day)
		const startDate = new Date(startDateTime.year, startDateTime.month - 1, startDateTime.day)
		const endDate = new Date(endDateTime.year, endDateTime.month - 1, endDateTime.day)

		if (schedule.startDate && currentDate < startDate) {
			return UserScheduleStatus.Upcoming
		} else if (schedule.startDate && currentDate >= startDate && schedule.endDate && currentDate <= endDate) {
			return UserScheduleStatus.Active
		}
		return null
	}
}

